import {
  Box,
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  NonCancelableCustomEvent,
  Pagination,
  Table,
  TableProps,
} from '@amzn/awsui-components-core';
import React from 'react';
import constants from '../../common/constants';
import CsvDownloadButton from '../comment/CsvDownloadButton';
import { DefaultTablePageSizeOptions } from './ContentWorkflowColumnDefinitions';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import TableSettingOptions from '../../model/TableSettingOptions';

export interface CustomPreferences {
  clearFilterOnRefresh?: boolean;
  stickyHeader?: boolean;
}

interface Props extends TableProps<ContentWorkflow> {
  items: ContentWorkflow[];
  csvFormat: Array<{ header: string; value: (item: ContentWorkflow) => any }>;
  tableContentSelectorOptions: CollectionPreferencesProps.ContentDisplayPreference;
  selectionType?: TableProps.SelectionType;
  onTableSettingChange: (
    event: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences>,
  ) => void;
  tablePreferences: TableSettingOptions;
}

const PaginatedTable: React.FunctionComponent<Props> = (props: Props) => {
  const csvDownloadButton = props.csvFormat ? (
    <CsvDownloadButton data={props.items} format={props.csvFormat} filename={`apg-content.csv`} />
  ) : null;

  const customPreference = (
    value: CustomPreferences,
    setCustomValue: React.Dispatch<CustomPreferences>,
  ) => {
    return (
      <div>
        <Box padding={{ bottom: 'm' }}>
          <Checkbox
            onChange={({ detail }) =>
              setCustomValue({
                stickyHeader: detail.checked,
                clearFilterOnRefresh: value.clearFilterOnRefresh,
              })
            }
            checked={!!value.stickyHeader}
            description={constants.STICKY_HEADER_TOOLTIP}
          >
            Sticky header
          </Checkbox>
        </Box>
        <Box padding={{ bottom: 'm' }}>
          <Checkbox
            onChange={({ detail }) =>
              setCustomValue({
                clearFilterOnRefresh: detail.checked,
                stickyHeader: value.stickyHeader,
              })
            }
            checked={!!value.clearFilterOnRefresh}
            description={constants.CLEAR_FILTERS_REFRESH_TOOLTIP}
          >
            Clear filters on refresh
          </Checkbox>
        </Box>
        {csvDownloadButton}
      </div>
    );
  };

  const preferences: CollectionPreferencesProps.Preferences<CustomPreferences> = {
    pageSize: props.tablePreferences.pageSize,
    wrapLines: props.tablePreferences.wrapLines,
    contentDisplay: props.columnDisplay,
    custom: {
      stickyHeader: props.tablePreferences.stickyHeader,
      clearFilterOnRefresh: props.tablePreferences.clearFiltersOnRefresh,
    },
  };

  const contentDisplayPreference: CollectionPreferencesProps.ContentDisplayPreference = {
    title: constants.TABLE_CONTENT_SELECTOR_TITLE,
    description: props.tableContentSelectorOptions?.description,
    options: props.tableContentSelectorOptions?.options ?? [],
  };

  const collectionPreferences = (
    <Box padding={{ bottom: 's' }}>
      <CollectionPreferences
        title={constants.PREFERENCES}
        confirmLabel={constants.CONFIRM_BUTTON_TEXT}
        cancelLabel={constants.CANCEL_BUTTON_TEXT}
        preferences={preferences}
        onConfirm={props.onTableSettingChange}
        pageSizePreference={{
          title: constants.TABLE_PAGE_SELECTOR_MESSAGE,
          options: DefaultTablePageSizeOptions,
        }}
        wrapLinesPreference={{
          label: constants.TABLE_WRAPLINES_LABEL,
          description: constants.TABLE_WRAPLINES_DESCRIPTION,
        }}
        contentDisplayPreference={contentDisplayPreference}
        customPreference={customPreference}
      />
    </Box>
  );

  const { items, collectionProps, paginationProps } = useCollection(props.items, {
    sorting: {},
    pagination: { pageSize: preferences.pageSize },
  });

  return (
    <Table
      {...props}
      {...collectionProps}
      items={items}
      preferences={collectionPreferences}
      pagination={
        <Pagination
          {...paginationProps}
          ariaLabels={{
            nextPageLabel: 'Next page',
            previousPageLabel: 'Previous page',
            pageLabel: (pageNumber) => `Page ${pageNumber}`,
          }}
        />
      }
      stickyHeader={!!props.tablePreferences.stickyHeader}
      wrapLines={!!props.tablePreferences.wrapLines}
      empty={props.empty}
      selectionType={props.selectionType ?? 'multi'}
      ariaLabels={{ tableLabel: 'Content table' }}
    />
  );
};

export default PaginatedTable;
